import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import bookingService from '../../services/bookingService';
import { useCurrency } from '../../context/CurrencyContext';
import InlineCurrencyPicker from '../InlineCurrencyPicker';
import { 
  X, Coffee, Calendar, Clock, CheckCircle, CreditCard, User, 
  Video, MapPin, Shield, Info, Loader2
} from 'lucide-react';

// Fika Fee - covers coffee/tea during in-person meeting (NOT a platform fee)
const FIKA_FEE = 199;
const FIKA_FEE_CURRENCY = 'SEK';

// Get tomorrow's date as YYYY-MM-DD
const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().slice(0, 10);
};

// Get minimum bookable date (tomorrow)
const getMinDate = () => getTomorrowDate();

export default function FikaBookingModal({ 
  isOpen, 
  onClose, 
  creative,
  service = null
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  
  // Form state
  const [scheduledAt, setScheduledAt] = useState('');
  const [fikaType, setFikaType] = useState('fika_online');
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getTomorrowDate());
  const [weekRange, setWeekRange] = useState(2);
  const [busySlots, setBusySlots] = useState([]);
  
  // API state
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [needLogin, setNeedLogin] = useState(false);
  
  // Pricing
  const creativeCurrency = creative?.currency || 'SEK';
  const creativeHourlyRate = Number(creative?.hourly_rate) || 0;
  const fikaFeeAmount = fikaType === 'fika_in_person' ? FIKA_FEE : 0;
  
  const convertedHourlyRate = convert(creativeHourlyRate, creativeCurrency, userCurrency);
  const convertedFikaFee = convert(fikaFeeAmount, FIKA_FEE_CURRENCY, userCurrency);
  const convertedTotal = convertedFikaFee + convertedHourlyRate;
  const loginUrl = `/login?redirect=${encodeURIComponent(location.pathname + (location.search || ''))}`;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setScheduledAt('');
      setSelectedDate(getTomorrowDate());
      setError('');
      setResult(null);
      setNeedLogin(false);
    }
  }, [isOpen]);

  // Fetch available slots and existing bookings
  useEffect(() => {
    if (!creative?.id || !isOpen) return;
    let active = true;
    
    async function loadData() {
      setLoadingSlots(true);
      try {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const horizon = new Date(tomorrow.getTime() + (weekRange === 1 ? 7 : 14) * 24 * 60 * 60 * 1000);
        
        const availRes = await apiGet('/availabilities', {
          creative_id: creative.id,
          ends_after: tomorrow.toISOString(),
          starts_before: horizon.toISOString(),
          limit: 200,
        });
        
        const list = availRes?.data?.availabilities || availRes?.availabilities || [];
        const filtered = list
          .filter((it) => (it?.kind || 'available') === 'available')
          .map((it) => ({
            id: it.id,
            start_at: it.start_at,
            end_at: it.end_at,
          }))
          .filter((it) => new Date(it.start_at) >= tomorrow)
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        
        try {
          const bookingsRes = await apiGet('/bookings', {
            creative_id: creative.user_id || creative.id,
            status: 'confirmed,pending',
            start_after: tomorrow.toISOString(),
            start_before: horizon.toISOString(),
            limit: 100,
          });
          const bookings = bookingsRes?.data?.bookings || bookingsRes?.bookings || [];
          if (active) {
            setBusySlots(bookings.map(b => ({
              start: new Date(b.start_at),
              end: new Date(b.end_at),
            })));
          }
        } catch (e) {
          console.error('Failed to load bookings:', e);
        }
        
        if (active) setSlots(filtered);
      } catch (e) {
        console.error('Failed to load slots:', e);
      } finally {
        if (active) setLoadingSlots(false);
      }
    }
    loadData();
    return () => { active = false; };
  }, [creative?.id, creative?.user_id, isOpen, weekRange]);

  const isSlotBusy = (slotStart) => {
    const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
    return busySlots.some(busy => slotStart < busy.end && slotEnd > busy.start);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('canceled') === '1') {
      setError('Payment was cancelled. You can try again.');
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);

    try {
      let userId = null;
      try {
        const me = await apiGet('/users/me');
        userId = me?.data?.user?.id || me?.user?.id || me?.data?.id || me?.id || null;
      } catch { void 0; }
      
      if (!userId) {
        setError('Please login to book');
        setNeedLogin(true);
        setLoading(false);
        return;
      }

      if (!scheduledAt) {
        setError('Please select an available time slot');
        setLoading(false);
        return;
      }

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const sel = new Date(scheduledAt);
      if (sel < tomorrow) {
        setError('Bookings must be at least 1 day in advance');
        setLoading(false);
        return;
      }

      const res = await bookingService.quickPay({
        creative_id: creative.id,
        service_id: service?.id || null,
        meeting_type: fikaType,
        scheduled_at: scheduledAt,
        hours: 1,
        notes: fikaType === 'fika_online' ? 'Online Fika Session' : 'In-Person Fika Meeting',
      });

      if (res?.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }

      if (!res?.payment_required) {
        setResult({
          booking: res?.booking,
          pricing: res?.pricing,
          status: 'confirmed',
          message: 'Your Fika session is confirmed!',
        });
      } else {
        setResult({
          booking: res?.booking,
          pricing: res?.pricing,
          status: 'pending',
          message: res?.message || 'Booking created. Complete payment to confirm.',
        });
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to book fika');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Success screen
  if (result?.status === 'confirmed') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-card-bg rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-card-border animate-in fade-in zoom-in duration-200">
          <div className="p-4 sm:p-6 text-center space-y-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
              <CheckCircle size={28} className="text-green-500" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-primary-text">Fika Booked!</h2>
            <p className="text-sm text-secondary-text">{result.message}</p>
            
            {result.booking && (
              <div className="bg-subtle-bg rounded-lg p-3 sm:p-4 text-left space-y-2 border border-card-border">
                <div className="text-sm">
                  <span className="text-secondary-text">Booking #: </span>
                  <span className="font-mono text-primary-text">{result.booking.booking_number}</span>
                </div>
                <div className="text-sm">
                  <span className="text-secondary-text">Date: </span>
                  <span className="text-primary-text">{new Date(result.booking.start_at).toLocaleDateString()}</span>
                </div>
                <div className="text-sm">
                  <span className="text-secondary-text">Time: </span>
                  <span className="text-primary-text">{new Date(result.booking.start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            )}

            {result.pricing?.escrow_amount > 0 && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-sm">
                <div className="flex items-center justify-center gap-2 text-blue-400">
                  <Shield size={14} />
                  <span className="text-xs sm:text-sm">{formatPrice(convert(result.pricing.escrow_amount, result.pricing?.currency || creativeCurrency, userCurrency), userCurrency)} held in escrow</span>
                </div>
              </div>
            )}

            <div className="flex gap-2 sm:gap-3 pt-2 sm:pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-3 sm:px-4 py-2 rounded-lg border border-card-border text-primary-text hover:bg-subtle-bg transition-colors text-sm"
              >
                Close
              </button>
              <button
                onClick={() => navigate('/dashboard/bookings')}
                className="flex-1 px-3 sm:px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm"
              >
                View Bookings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card-bg rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-hidden border border-card-border animate-in fade-in zoom-in duration-200 flex flex-col">
        {/* Header */}
        <div className="border-b border-card-border px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Coffee size={18} className="text-blue-500" />
              </div>
              <div>
                <h2 className="font-semibold text-primary-text text-sm sm:text-base">Book a Fika</h2>
                <p className="text-xs sm:text-sm text-secondary-text">Quick chat over coffee</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-subtle-bg transition-colors"
              aria-label="Close"
            >
              <X size={20} className="text-secondary-text" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-6 space-y-4">
          {/* Mobile Price Summary */}
          <div className="sm:hidden p-3 bg-subtle-bg rounded-lg border border-card-border">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-secondary-text">Total Price</div>
                <div className="text-lg font-bold text-primary-text">{formatPrice(convertedTotal, userCurrency)}</div>
              </div>
              <InlineCurrencyPicker />
            </div>
          </div>

          {/* Creative Info */}
          {creative && (
            <div className="flex items-center gap-3 pb-3 sm:pb-4 border-b border-card-border">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                {creative.user?.avatar_url || creative.avatar_url ? (
                  <img src={creative.user?.avatar_url || creative.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User size={20} className="text-white" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-primary-text text-sm sm:text-base truncate">
                  {creative.user?.display_name || creative.display_name || creative.name}
                </div>
                <div className="text-xs sm:text-sm text-secondary-text truncate">{creative.professional_title || creative.headline}</div>
                {creativeHourlyRate > 0 && (
                  <div className="text-xs sm:text-sm text-green-500 font-medium">
                    {formatPrice(convertedHourlyRate, userCurrency)}/hour
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fika Type */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-text">Fika Type</label>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setFikaType('fika_online')}
                className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border transition-all text-sm ${
                  fikaType === 'fika_online'
                    ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                    : 'border-card-border bg-subtle-bg text-secondary-text hover:border-blue-400'
                }`}
              >
                <Video size={16} />
                <span>Online</span>
              </button>
              <button
                type="button"
                onClick={() => setFikaType('fika_in_person')}
                className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border transition-all text-sm ${
                  fikaType === 'fika_in_person'
                    ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                    : 'border-card-border bg-subtle-bg text-secondary-text hover:border-blue-400'
                }`}
              >
                <MapPin size={16} />
                <span>In-Person</span>
              </button>
            </div>
          </div>

          {/* Time Slot Selection */}
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="text-sm font-medium text-secondary-text flex items-center gap-2">
                <Calendar size={14} />
                Select Time Slot
              </label>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  type="button"
                  className={`px-2 sm:px-3 py-1 text-xs rounded transition-colors ${
                    weekRange === 1 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-subtle-bg text-secondary-text hover:bg-card-border'
                  }`}
                  onClick={() => setWeekRange(1)}
                >
                  1 Week
                </button>
                <button
                  type="button"
                  className={`px-2 sm:px-3 py-1 text-xs rounded transition-colors ${
                    weekRange === 2 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-subtle-bg text-secondary-text hover:bg-card-border'
                  }`}
                  onClick={() => setWeekRange(2)}
                >
                  2 Weeks
                </button>
              </div>
            </div>

            {loadingSlots && (
              <div className="flex items-center gap-2 text-xs sm:text-sm text-secondary-text">
                <Loader2 size={14} className="animate-spin" />
                Loading available slots...
              </div>
            )}

            {/* Pre-configured slots */}
            {!loadingSlots && slots.length > 0 && (
              <div className="grid grid-cols-2 gap-2 max-h-32 sm:max-h-40 overflow-y-auto">
                {slots.map((s) => {
                  const start = new Date(s.start_at);
                  const end = new Date(s.end_at);
                  const dateLabel = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                  const timeLabel = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}–${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
                  const selected = scheduledAt && new Date(scheduledAt).toISOString() === start.toISOString();
                  const isBusy = isSlotBusy(start);
                  
                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={isBusy}
                      onClick={() => !isBusy && setScheduledAt(start.toISOString())}
                      className={`p-2 rounded-lg border text-xs sm:text-sm transition-all ${
                        isBusy
                          ? 'border-card-border bg-subtle-bg/50 text-secondary-text/50 cursor-not-allowed opacity-50'
                          : selected
                            ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                            : 'border-card-border bg-subtle-bg text-primary-text hover:border-blue-400'
                      }`}
                    >
                      <div className="font-medium text-xs">{dateLabel}</div>
                      <div className="text-xs text-secondary-text">
                        {isBusy ? 'Unavailable' : timeLabel}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Manual date/time selection */}
            {!loadingSlots && slots.length === 0 && (
              <div className="space-y-2 sm:space-y-3">
                <p className="text-xs text-secondary-text">No pre-configured slots. Select a date and time:</p>
                <input
                  className="input w-full text-sm"
                  type="date"
                  value={selectedDate}
                  min={getMinDate()}
                  onChange={(e) => {
                    const newDate = e.target.value || getTomorrowDate();
                    setSelectedDate(newDate);
                    setScheduledAt('');
                  }}
                />
                {selectedDate && (
                  <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                    {Array.from({ length: 8 }).map((_, i) => {
                      const hour = 9 + i;
                      const start = new Date(`${selectedDate}T${String(hour).padStart(2, '0')}:00:00`);
                      const selected = scheduledAt && new Date(scheduledAt).toISOString() === start.toISOString();
                      const timeLabel = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                      const isBusy = isSlotBusy(start);
                      
                      return (
                        <button
                          key={`slot-${selectedDate}-${hour}`}
                          type="button"
                          disabled={isBusy}
                          onClick={() => !isBusy && setScheduledAt(start.toISOString())}
                          className={`py-1.5 sm:py-2 rounded border text-xs sm:text-sm transition-all ${
                            isBusy
                              ? 'border-card-border bg-subtle-bg/50 text-secondary-text/50 cursor-not-allowed line-through opacity-50'
                              : selected
                                ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                                : 'border-card-border bg-subtle-bg text-primary-text hover:border-blue-400'
                          }`}
                        >
                          {timeLabel}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pricing Breakdown - Desktop version with currency picker */}
          {scheduledAt && (
            <div className="rounded-lg border border-card-border p-3 sm:p-4 bg-subtle-bg">
              {/* Currency Picker - Hidden on mobile since we show it at top */}
              <div className="hidden sm:flex items-center justify-between mb-3 pb-2 border-b border-card-border">
                <span className="text-xs text-secondary-text">Prices shown in</span>
                <InlineCurrencyPicker />
              </div>
              
              <div className="space-y-2">
                {fikaType === 'fika_in_person' && (
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-1">
                      <Coffee size={14} className="text-blue-500" />
                      <span className="text-secondary-text">Fika Fee</span>
                    </div>
                    <span className="font-medium text-primary-text">{formatPrice(convertedFikaFee, userCurrency)}</span>
                  </div>
                )}
                {creativeHourlyRate > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-secondary-text">Session Fee</span>
                      <Shield size={12} className="text-green-500" />
                    </div>
                    <span className="font-medium text-primary-text">{formatPrice(convertedHourlyRate, userCurrency)}</span>
                  </div>
                )}
                <div className="border-t border-card-border pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-primary-text">Total</span>
                    <span className="text-base sm:text-lg font-bold text-primary-text">{formatPrice(convertedTotal, userCurrency)}</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-2 sm:mt-3 flex items-start gap-2 text-xs text-secondary-text bg-card-bg rounded p-2 border border-card-border">
                {fikaType === 'fika_in_person' ? (
                  <>
                    <Coffee size={12} className="mt-0.5 flex-shrink-0 text-blue-500" />
                    <span>Fika fee covers refreshments. Session fee held in escrow.</span>
                  </>
                ) : (
                  <>
                    <Video size={12} className="mt-0.5 flex-shrink-0 text-blue-500" />
                    <span>Online Fika - no additional fees. Session fee held in escrow.</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-xs sm:text-sm text-red-400 bg-red-500/10 border border-red-500/20 p-2 sm:p-3 rounded-lg">{error}</div>
          )}
        </form>

        {/* Footer - Always visible */}
        <div className="border-t border-card-border px-4 sm:px-6 py-3 sm:py-4 bg-subtle-bg flex-shrink-0 space-y-2">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !scheduledAt}
            className="w-full py-2.5 sm:py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard size={16} />
                Book & Pay {formatPrice(convertedTotal, userCurrency)}
              </>
            )}
          </button>

          {needLogin && (
            <button
              type="button"
              className="w-full py-2.5 sm:py-3 rounded-lg border border-card-border text-primary-text hover:bg-card-bg transition-colors text-sm"
              onClick={() => navigate(loginUrl)}
            >
              Log in to continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
